// @ts-ignore - TypeScript has issues with Pinia imports, but they work at runtime
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '@/services/api';
import { safeStorage, checkStorageAndWarn } from '@/utils/storage';
import { setGlobalToken } from '@/utils/tokenCache';
import type { Restaurant } from '@/composables/useRestaurant';

// Types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData extends LoginCredentials {
  full_name: string;
  role?: string;
}

export interface User {
  id: string | number;
  email: string;
  full_name: string;
  role: string;
  staff_type?: string | null;
  is_active: boolean;
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null);
  const restaurant = ref<Restaurant | null>(null);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);
  const accessToken = ref<string | null>(null); // In-memory token for Safari
  const isAuthenticated = computed<boolean>(() => !!user.value);
  const isAdmin = computed<boolean>(() => user.value?.role === 'admin');

  const getStoredValue = (key: string) => safeStorage.getItem(key) || safeStorage.getItem(key, true);
  const getStoredAccessToken = () => getStoredValue('access_token');
  const getStoredUser = () => getStoredValue('user');

  const setStoredValue = (key: string, value: string, includeSession = false) => {
    safeStorage.setItem(key, value);
    if (includeSession) safeStorage.setItem(key, value, true);
  };

  const removeStoredValue = (key: string, includeSession = true) => {
    safeStorage.removeItem(key);
    if (includeSession) safeStorage.removeItem(key, true);
  };

  const clearStoredUser = () => removeStoredValue('user');

  const clearStoredTokens = (includeRefreshToken = true) => {
    removeStoredValue('access_token');
    if (includeRefreshToken) removeStoredValue('refresh_token');
  };

  const setInMemoryToken = (token: string | null) => {
    accessToken.value = token;
    setGlobalToken(token);
  };

  const persistUser = (userData: User, includeSession = false) =>
    setStoredValue('user', JSON.stringify(userData), includeSession);

  const persistTokens = (accessTokenValue: string, refreshTokenValue?: string, includeSession = false) => {
    setStoredValue('access_token', accessTokenValue, includeSession);
    if (refreshTokenValue) {
      setStoredValue('refresh_token', refreshTokenValue, includeSession);
    }
  };

  const fetchCurrentUser = async (token: string): Promise<User | null> => {
    const response = await api.get('/users/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response ? (response as unknown as User) : null;
  };

  const loadRestaurantSafely = async (context: 'login' | 'auth check') => {
    try {
      await loadRestaurant();
    } catch (restaurantError) {
      console.warn(`⚠️ Failed to load restaurant data on ${context}:`, restaurantError);
    }
  };
  
  // Actions
  async function login(credentials: LoginCredentials, remember: boolean = true) {
    try {
      loading.value = true;
      error.value = null;
      
      // Use OAuth2 password flow
      const formData = new URLSearchParams();
      formData.append('username', credentials.email);
      formData.append('password', credentials.password);
      formData.append('grant_type', 'password');
      formData.append('scope', '');

      const response = await api.post('/auth/token', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }) as any;

      const { access_token, refresh_token } = response;
      
      if (!access_token) {
        throw new Error('No access token received');
      }
      
      // Store tokens in memory FIRST (for Safari)
      setInMemoryToken(access_token);
      
      persistTokens(access_token, refresh_token);
      
      try {
        const userData = await fetchCurrentUser(access_token);
        
        if (userData) {
          user.value = userData;
          persistUser(userData, true);
          persistTokens(access_token, refresh_token, true);
          
          // Wait to ensure Safari persists the data
          await new Promise(resolve => setTimeout(resolve, 300));
          await loadRestaurantSafely('login');
          
          // Note: Redirect is now handled by LoginView.vue component
          return true;
        }
      } catch (userError) {
        console.error('❌ Failed to fetch user data:', userError);
        error.value = 'Failed to load user data';
        // Clear tokens if user fetch fails
        setInMemoryToken(null);
        clearStoredTokens(true);
        clearStoredUser();
        return false;
      }
    } catch (err: any) {
      // Extract error message from new centralized error format
      error.value = err.response?.data?.error?.message || err.response?.data?.detail || 'Login failed';
      return false;
    } finally {
      loading.value = false;
    }
  }
  
  async function register(registerData: RegisterData) {
    try {
      loading.value = true;
      error.value = null;
      
      await api.post('/auth/register', {
        email: registerData.email,
        password: registerData.password,
        full_name: registerData.full_name,
        role: registerData.role || 'customer',
      });
      
      // Auto-login after registration
      return await login({
        email: registerData.email,
        password: registerData.password
      });
    } catch (err: any) {
      // Extract error message from new centralized error format
      error.value = err.response?.data?.error?.message || err.response?.data?.detail || 'Registration failed';
      return false;
    } finally {
      loading.value = false;
    }
  }
  
  async function logout() {
    try {
      loading.value = true;
      
      // Call backend logout to clear HTTPOnly cookies
      try {
        await api.post('/auth/logout');
      } catch (err) {
        console.warn('Backend logout failed, continuing with client cleanup:', err);
      }
      
      // Clear user state
      user.value = null;
      setInMemoryToken(null);
      
      // Clear tokens from storage
      clearStoredTokens(true);
      clearStoredUser();
      
      // Note: Navigation is handled by the calling component (e.g., AppNavbar.vue)
      // Store should not handle routing directly
    } catch (error) {
      console.error('Error during logout:', error);
      // Even if there's an error, ensure we're logged out
      user.value = null;
      setInMemoryToken(null);
    } finally {
      loading.value = false;
    }
  }
  
  async function checkAuth() {
    try {
      loading.value = true;
      const token = getStoredAccessToken();
      
      if (!token) {
        // No token means not authenticated
        user.value = null;
        clearStoredUser();
        return false;
      }

      // Restore token to memory cache (important for Safari)
      setInMemoryToken(token);
      
      const userData = await fetchCurrentUser(token);
      
      if (userData) {
        user.value = userData;
        persistUser(userData);
        await loadRestaurantSafely('auth check');
        return true;
      }
      
      // If no user data, clear auth state
      user.value = null;
      clearStoredUser();
      clearStoredTokens(false);
      return false;
    } catch (err: any) {
      console.error('Auth check failed:', err);
      
      // Only clear tokens if it's a 401 (unauthorized) error
      // Don't clear on network errors or other temporary issues
      if (err.response?.status === 401) {
        user.value = null;
        setInMemoryToken(null);
        clearStoredUser();
        clearStoredTokens(true);
      } else {
        // For other errors (network, 500, etc), keep the token
        // Just clear the user from memory
        user.value = null;
      }
      
      return false;
    } finally {
      loading.value = false;
    }
  }
  
  // Load restaurant information
  async function loadRestaurant() {
    try {
      restaurant.value = await api.get('/restaurants/current');
    } catch (err) {
      console.error('Failed to load restaurant:', err);
      restaurant.value = null;
    }
  }
  
  // Initialize the store
  function init() {
    // Check storage availability and warn if needed
    checkStorageAndWarn();
    
    // Get token and user from safe storage
    const token = getStoredAccessToken();
    const storedUser = getStoredUser();
    
    if (!token) {
      if (storedUser) clearStoredUser();
      user.value = null;
      return;
    }

    setInMemoryToken(token);

    if (!storedUser) {
      user.value = null;
      return;
    }

    try {
      user.value = JSON.parse(storedUser);
    } catch (err) {
      console.error('Failed to parse stored user data:', err);
      clearStoredUser();
      clearStoredTokens(false);
      user.value = null;
    }
  }
  
  // Initialize on store creation
  init();
  
  return {
    // State
    user,
    restaurant,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    
    // Actions
    login,
    register,
    logout,
    checkAuth,
    loadRestaurant,
  };
});

// Export the store instance
export default useAuthStore;
